import { useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function DeleteCommissionDialog({ commission, open, onOpenChange, onSuccess }) {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    try {
      setDeleting(true);
      
      const response = await axios.post(
        'https://api1.simplyworkcrm.com/api:JBYRWqH3/portal/internal/commission/delete',
        {
          commission_id: commission.id
        }
      );

      toast.success('Commission deleted successfully');
      onSuccess && onSuccess();
      onOpenChange(false);
    } catch (err) {
      console.error('Error deleting commission:', err);
      toast.error('Failed to delete commission');
    } finally {
      setDeleting(false);
    }
  };

  if (!commission) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-5 h-5" />
            Delete Commission
          </DialogTitle>
          <DialogDescription>
            This action cannot be undone. Are you sure you want to delete this commission?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-neutral-600">Commission Amount:</span>
              <span className={`text-sm font-bold ${parseFloat(commission.commission_amount) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                ${parseFloat(commission.commission_amount || 0).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-neutral-600">Policy Number:</span>
              <span className="text-sm font-semibold">{commission.policy_number}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-neutral-600">Paid Status:</span>
              <span className="text-sm font-semibold">{commission.paid_split}</span>
            </div>
            {commission.comment && (
              <div>
                <span className="text-sm text-neutral-600">Comment:</span>
                <p className="text-sm mt-1 text-neutral-800">{commission.comment}</p>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={deleting}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {deleting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Deleting...
              </>
            ) : (
              'Delete Commission'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
